import React from 'react';
import { Input, Modal, Spin, Button, Tooltip } from 'antd';
import { FixedSizeList as List } from 'react-window';
import { Plus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuditorStoresModal } from './auditorStores.controller';

export default function AuditorStoresModal({ isOpen, setIsOpen, allStores, userId, updateStores }) {
    const {
        search,
        loading,
        isSearching,
        currentPage,
        setCurrentPage,
        totalPages,
        totalStores,
        displayedStores,
        searchResults,
        listedStores,
        selectedCount,
        onSearch,
        isSelected,
        toggleStore,
        save
    } = useAuditorStoresModal({ isOpen, allStores, userId, updateStores });

    // ✅ Virtualized Row Component for Performance
    const Row = React.memo(({ index, style, data }) => {
        const store = data[index];
        const selected = isSelected(store);
        return (
            <div style={style} className="flex items-center justify-between gap-3 px-3 border-b border-surface-100">
                <div className="min-w-0">
                    <p className="m-0 truncate text-sm font-medium text-surface-900">{store.name}</p>
                    <p className="m-0 text-xs text-surface-400 tabular-nums">{store.rif}</p>
                </div>
                <Tooltip title={selected ? 'Quitar del auditor' : 'Asignar al auditor'}>
                    <Button
                        size="small"
                        type={selected ? 'default' : 'text'}
                        danger={selected}
                        icon={selected ? <X /> : <Plus />}
                        aria-label={selected ? 'Quitar del auditor' : 'Asignar al auditor'}
                        onClick={() => toggleStore(store)}
                    />
                </Tooltip>
            </div>
        );
    });

    return (
        <Modal
            title="Pisos de Venta"
            open={isOpen}
            cancelText="Cancelar"
            okText="Guardar cambios"
            onCancel={() => setIsOpen(false)}
            onOk={() => save()}
            width={600}>
            <div className="flex flex-col">
                <p className="m-0 mb-3 text-sm text-surface-500">
                    Asigna o quita pisos de venta. <span className="font-medium text-surface-700 tabular-nums">{selectedCount}</span> seleccionados.
                </p>
                <Input.Search
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder="Buscar piso de venta por nombre, RIF o cliente"
                    loading={isSearching}
                    allowClear
                />
                <div className="mt-3 h-96 rounded-lg border border-surface-200 overflow-hidden">
                    {loading ? (
                        <div className="flex justify-center items-center h-full">
                            <Spin />
                        </div>
                    ) : (
                        <List
                            height={382} // h-96 minus the 1px borders
                            itemCount={listedStores.length}
                            itemSize={70} // Approximate height of each row
                            width="100%"
                            itemData={listedStores}>
                            {Row}
                        </List>
                    )}
                </div>
                {/* Pagination Controls */}
                {search === '' && totalPages > 1 && (
                    <div className="mt-3 flex justify-between items-center">
                        <Button size="small" icon={<ChevronLeft />} disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                            Anterior
                        </Button>

                        <span className="text-sm text-surface-500 tabular-nums">
                            Página {currentPage} de {totalPages}
                        </span>

                        <Button size="small" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                            Siguiente
                            <ChevronRight />
                        </Button>
                    </div>
                )}

                <div className="mt-2 text-xs text-surface-500 tabular-nums">
                    {search !== '' ? (
                        <>Mostrando {searchResults?.length ?? 0} resultados de búsqueda</>
                    ) : (
                        <>
                            Mostrando {displayedStores?.length ?? 0} de {totalStores} pisos de venta
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
}
